import { inject, Injectable, REQUEST } from '@angular/core';
import { EnvService, LocaleConfig, SsrService } from '@hrra/core';

/**
 * Single source of truth for the language URL prefix scheme.
 *
 * The default language is served at the site root with NO URL prefix; every
 * other supported language is exposed under a `/<locale>/` path prefix (e.g.
 * `/en/`). The supported set and the default language are tenant-specific and
 * come from the tenant config (loaded by the app initializer into EnvService),
 * so this service reads them from `EnvService.localeConfig` rather than
 * hardcoding them.
 *
 * App code injects this service and calls its methods; the underlying
 * `LocaleConfig` is never passed around by callers.
 */
@Injectable({ providedIn: 'root' })
export class LocaleService {
  private env = inject(EnvService);
  private ssr = inject(SsrService);
  private request = inject(REQUEST, { optional: true });

  private get config(): LocaleConfig {
    return this.env.localeConfig;
  }

  /** Languages exposed under a `/<locale>/` prefix — every supported one except the default. */
  private getPrefixedLocales(): string[] {
    return this.config.supportedLocales.filter((locale) => locale !== this.config.defaultLocale);
  }

  /**
   * True if the locale uses a `/<locale>/` URL prefix (supported and not the default).
   * Throws on an unsupported locale — callers are expected to pass a real locale.
   */
  private isPrefixed(locale: string): boolean {
    if (!this.config.supportedLocales.includes(locale)) {
      throw new Error(`Unsupported locale: '${locale}'`);
    }
    return locale !== this.config.defaultLocale;
  }

  /** Split a url/path into the path portion and any `?query`/`#hash` suffix. */
  private splitUrl(url: string): { path: string; suffix: string } {
    const value = url || '';
    const i = value.search(/[?#]/);
    return i === -1
      ? { path: value, suffix: '' }
      : { path: value.slice(0, i), suffix: value.slice(i) };
  }

  /** Path broken into its non-empty segments. `/en/jobs/` → ['en', 'jobs']. */
  private getSegments(path: string): string[] {
    return path.split('/').filter((segment) => segment.length > 0);
  }

  /**
   * The prefixed locale a path begins with, or `null` when it has no locale
   * prefix (i.e. it targets the default language at the root).
   */
  private getLeadingLocale(path: string): string | null {
    const segments = this.getSegments(path);
    if (segments.length === 0) {
      return null;
    }
    const candidate = segments[0].toLowerCase();
    return this.getPrefixedLocales().includes(candidate) ? candidate : null;
  }

  /** Path (no origin) of the current request (server) or browser location. */
  getCurrentPath(): string {
    if (this.ssr.isPlatformBrowser()) {
      return window.location.pathname + window.location.search + window.location.hash;
    }
    return this.request ? new URL(this.request.url).pathname : '/';
  }

  /** Public origin (scheme + host) of the current request/location, used for absolute SEO links. */
  getOrigin(): string {
    if (this.ssr.isPlatformBrowser()) {
      return window.location.origin;
    }
    if (this.request === null) {
      return '';
    }
    const host = this.request.headers.get('host');
    const forwardedProto = this.request.headers.get('x-forwarded-proto');
    const protocol = forwardedProto
      ? forwardedProto.split(',')[0].trim()
      : new URL(this.request.url).protocol.replace(':', '');
    return `${protocol}://${host}`;
  }

  getSupportedLocales(): string[] {
    return this.config.supportedLocales;
  }

  getDefaultLocale(): string {
    return this.config.defaultLocale;
  }

  /**
   * The locale implied by a URL: the leading `/<locale>/` segment if present,
   * otherwise the default language. Defaults to the current URL.
   */
  getLocale(path: string = this.getCurrentPath()): string {
    const locale = this.getLeadingLocale(this.splitUrl(path).path);
    return locale === null ? this.config.defaultLocale : locale;
  }

  /** The URL prefix segment for a locale: `'en/'` for prefixed languages, `''` for the default. */
  getLocalePrefix(locale: string = this.getLocale()): string {
    return this.isPrefixed(locale) ? `${locale}/` : '';
  }

  /**
   * Remove any leading prefixed-locale segment, returning the "logical" path the
   * Angular router actually matches. Redundant slashes are normalised away; any
   * `?query`/`#hash` suffix is preserved. The result always starts with a single
   * leading slash.
   */
  stripLocale(path: string): string {
    const { path: pathOnly, suffix } = this.splitUrl(path);
    const segments = this.getSegments(pathOnly);

    if (this.getLeadingLocale(pathOnly) !== null) {
      segments.shift();
    }

    return '/' + segments.join('/') + suffix;
  }

  /**
   * Re-attach a locale prefix to a path (the inverse of `stripLocale`). The
   * default language yields the bare logical path; a prefixed language gets
   * `/<locale>` prepended. The `?query`/`#hash` suffix is preserved.
   */
  addLocale(path: string, locale: string): string {
    const stripped = this.stripLocale(path); // begins with '/', keeps suffix
    return this.isPrefixed(locale) ? `/${locale}` + stripped : stripped;
  }

  /**
   * If a path carries the default locale as a redundant prefix (e.g. `/ka/jobs`
   * when `ka` is the default), the canonical prefix-less path to redirect to;
   * otherwise `null`. Used by the wildcard redirect guard to emit a 301.
   */
  getDefaultPrefixRedirect(path: string): string | null {
    const { path: pathOnly, suffix } = this.splitUrl(path);
    const segments = this.getSegments(pathOnly);

    if (segments.length === 0 || segments[0].toLowerCase() !== this.config.defaultLocale) {
      return null;
    }

    segments.shift();
    return '/' + segments.join('/') + suffix;
  }
}
